import { Modal, SelectInput, Stack } from "@ucchub/ui";
import type { SelectOption } from "@ucchub/ui";
import { useState } from "react";
import Heading from "@vfuk/core-heading";
import Spacing from "@vfuk/core-spacing";
import Button from "@vfuk/core-button";
import { useTranslation } from "react-i18next";
import type { Product } from "./Customer.types";

export type AddProductModalProps = {
  isProductsLoading?: boolean;
  isOpen: boolean;
  customerId: string;
  productList: Product[];
  onClose?: () => void;
};

const generateGetOptionKey =
  <T extends object>(key: keyof T) =>
  (option: T) => {
    return option[key];
  };

const getProductOptionLabel = generateGetOptionKey<Product>("name");
const getProductOptionValue = generateGetOptionKey<Product>("id");

const AddProductModal = ({
  isProductsLoading = false,
  isOpen,
  productList,
  customerId,
  onClose,
}: AddProductModalProps) => {
  const [selectedProduct, setSelectedProduct] = useState<SelectOption>();
  const { t } = useTranslation();

  const onChangeProduct = (option: SelectOption) => {
    setSelectedProduct(option);
  };

  const handleOnClose = () => {
    setSelectedProduct(undefined);
    onClose?.();
  };

  const proceedBtnIsEnabled = !!selectedProduct;
  const proceedBtnStatus = proceedBtnIsEnabled ? undefined : "disabled";

  return (
    <Modal
      srName={t("core:customers:product")}
      isOpen={isOpen}
      size={2}
      animate
      isClosable
      onClose={handleOnClose}
    >
      <Stack direction="column">
        <Heading level={5} weight={3}>
          {t("core:customers:add_product")}
        </Heading>
      </Stack>
      <Spacing
        spacingLevel={{
          top: 8,
          bottom: 5,
        }}
      >
        <SelectInput
          label={t("core:customers:product")}
          spaceForError={false}
          options={productList}
          value={selectedProduct}
          isLoading={isProductsLoading}
          onChange={onChangeProduct}
          getOptionLabel={getProductOptionLabel}
          getOptionValue={getProductOptionValue}
        />
      </Spacing>
      <Stack justifyContent="center" columnGap="0.8rem">
        <Button
          text={t("core:customers:cancel")}
          appearance="alt1"
          onClick={handleOnClose}
        />
        <Button
          text={t("core:customers:proceed")}
          appearance="primary"
          state={proceedBtnStatus}
          customRouterProps={
            selectedProduct && {
              to: `/customers/${customerId}/product-config/${selectedProduct.id}/add`,
            }
          }
        />
      </Stack>
    </Modal>
  );
};

export default AddProductModal;
